import { useState, useEffect } from 'react';
import { LogOut, Plus, List, Settings, Users, Receipt, ClipboardCheck, UserCog } from 'lucide-react';
import { supabase } from './lib/supabase';
import { Auth } from './components/Auth';
import { CreateTicketForm } from './components/CreateTicketForm';
import { TicketList } from './components/TicketList';
import { TicketDetail } from './components/TicketDetail';
import { PropertyManager } from './components/PropertyManager';
import { PersonnelManager } from './components/PersonnelManager';
import { InvoiceSettingsManager } from './components/InvoiceSettingsManager';
import { InspectionManager } from './components/InspectionManager';
import { UserManagement } from './components/UserManagement';

type View = 'list' | 'create' | 'properties' | 'personnel' | 'invoice-settings' | 'inspections' | 'user-management';

function App() {
  const [currentView, setCurrentView] = useState<View>('list');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();
        setIsAdmin(profile?.role === 'admin');
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();
        setIsAdmin(profile?.role === 'admin');
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleTicketCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
    setCurrentView('list');
  };

  const handleTicketUpdated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Auth onAuthSuccess={() => {}} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <nav className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Company Logo" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-gray-800">CO Hospitality House Keeping</h1>
                <p className="text-xs text-gray-500">Management System</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                title="Sign Out"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
              <button
                onClick={() => setCurrentView('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'list'
                    ? 'bg-secondary-50 text-secondary-700 border border-secondary-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List size={18} />
                <span className="hidden sm:inline">Tickets</span>
              </button>
              <button
                onClick={() => setCurrentView('create')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'create'
                    ? 'bg-accent-100 text-accent-700 border border-accent-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Plus size={18} />
                <span className="hidden sm:inline">New Ticket</span>
              </button>
              <button
                onClick={() => setCurrentView('properties')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'properties'
                    ? 'bg-secondary-50 text-secondary-700 border border-secondary-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Settings size={18} />
                <span className="hidden sm:inline">Properties</span>
              </button>
              <button
                onClick={() => setCurrentView('personnel')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'personnel'
                    ? 'bg-secondary-50 text-secondary-700 border border-secondary-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Users size={18} />
                <span className="hidden sm:inline">Personnel</span>
              </button>
              <button
                onClick={() => setCurrentView('invoice-settings')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'invoice-settings'
                    ? 'bg-secondary-50 text-secondary-700 border border-secondary-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Receipt size={18} />
                <span className="hidden sm:inline">Invoices</span>
              </button>
              <button
                onClick={() => setCurrentView('inspections')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'inspections'
                    ? 'bg-secondary-50 text-secondary-700 border border-secondary-300'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ClipboardCheck size={18} />
                <span className="hidden sm:inline">Inspections</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => setCurrentView('user-management')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    currentView === 'user-management'
                      ? 'bg-secondary-50 text-secondary-700 border border-secondary-300'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <UserCog size={18} />
                  <span className="hidden sm:inline">Users</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentView === 'list' && (
          <TicketList
            onSelectTicket={setSelectedTicketId}
            refreshTrigger={refreshTrigger}
          />
        )}
        {currentView === 'create' && (
          <CreateTicketForm onSuccess={handleTicketCreated} />
        )}
        {currentView === 'properties' && <PropertyManager />}
        {currentView === 'personnel' && <PersonnelManager />}
        {currentView === 'invoice-settings' && <InvoiceSettingsManager />}
        {currentView === 'inspections' && <InspectionManager />}
        {currentView === 'user-management' && isAdmin && <UserManagement />}
      </main>

      {selectedTicketId && (
        <TicketDetail
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(null)}
          onUpdate={handleTicketUpdated}
        />
      )}
    </div>
  );
}

export default App;
